import React, { useState } from "react";
import type { FindingGroup } from "@shared/site-audit-report-view";
import { PresentedCopy } from "./presented-text";

const FILTERS = ["all", "critical", "high", "medium", "low", "other"] as const;
type FindingFilter = (typeof FILTERS)[number];

export function activeFindingFilter(filter: FindingFilter, groups: FindingGroup[]): FindingFilter {
  if (filter === "all") return "all";
  return groups.some((group) => group.severity === filter) ? filter : "all";
}

export function FindingList({
  groups,
  initialFilter = "all",
}: {
  groups: FindingGroup[];
  initialFilter?: FindingFilter;
}) {
  const [filter, setFilter] = useState<FindingFilter>(initialFilter);
  if (groups.length === 0) return null;
  const active = activeFindingFilter(filter, groups);
  const visible = active === "all" ? groups : groups.filter((group) => group.severity === active);
  const counts = new Map(groups.map((group) => [group.severity, group.items.length]));
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filter findings">
        {FILTERS.filter((key) => key === "all" || counts.has(key)).map((key) => {
          const count = key === "all" ? total : counts.get(key) ?? 0;
          const label = key === "all" ? "All" : groups.find((group) => group.severity === key)?.label ?? key;
          const selected = active === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={selected}
              onClick={() => setFilter(key)}
              className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                selected ? "border-primary text-primary" : "border-border text-foreground"
              }`}
            >
              {label} {count}
            </button>
          );
        })}
      </div>
      {visible.map((group) => (
        <div key={group.severity} className="space-y-3">
          <h3 className={`text-sm font-semibold ${severityClass(group.severity)}`}>
            {group.label} ({group.items.length})
          </h3>
          <ul className="space-y-3">
            {group.items.map((item, index) => (
              <li key={`${group.severity}-${index}`} className="border-t border-border pt-3">
                <p className={`text-xs font-semibold uppercase tracking-wide ${severityClass(group.severity)}`}>
                  {group.label}
                </p>
                <PresentedCopy value={item.title} className="mt-1 text-sm font-medium" />
                <PresentedCopy value={item.detail} className="mt-1 text-sm leading-relaxed text-foreground/75" />
                {item.category ? <p className="mt-1 text-xs text-foreground/75">{item.category}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function severityClass(severity: FindingGroup["severity"]): string {
  if (severity === "critical") return "text-destructive";
  if (severity === "high") return "text-chart-3";
  if (severity === "medium") return "text-[hsl(211_72%_36%)] dark:text-[hsl(211_80%_74%)]";
  return "text-foreground/75";
}
